// ═══════════════════════════════════════════════════════════════
// SPEKTRIX IMPORT MODAL
// Path: src/components/features/SpektrixImportModal.jsx
//
// Multi-step wizard for importing Spektrix CSV exports.
// Step 1: Upload → Step 2: Preview/Dedup → Step 3: Duplicate Review
// → Step 4: Options & Import → Step 5: Results
//
// Blueprint: CMM_Spektrix_Import_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback, useEffect } from "react";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Loader,
  X,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, CHAPTER_ID, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import {
  parseCSV,
  autoMapColumns,
  analyzeImport,
  diagnoseImport,
  filterAnalysisByDate,
  findDuplicateGifts,
  executeImport,
} from "../../services/importService";

const STEP_LABELS = ["Upload", "Preview", "Duplicates", "Import", "Results"];

export const SpektrixImportModal = ({ onClose, preloadedContent, preloadedFileName, batchId, onComplete }) => {
  const { palette, currentUser, showMessage, pathways, bumpDataVersion } = useApp();

  const [step, setStep] = useState(1);

  // Step 1 state
  const [fileContent, setFileContent] = useState(preloadedContent || null);
  const [fileName, setFileName] = useState(preloadedFileName || "");

  // Step 2 state
  const [analysis, setAnalysis] = useState(null);
  const [fullAnalysis, setFullAnalysis] = useState(null); // unfiltered
  const [analyzing, setAnalyzing] = useState(false);
  const [diagnostic, setDiagnostic] = useState(null);
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Step 3 state — duplicate review
  const [duplicates, setDuplicates] = useState([]);
  const [scanningDuplicates, setScanningDuplicates] = useState(false);
  const [skipSet, setSkipSet] = useState(new Set());

  // Step 4 state
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [defaultPathway, setDefaultPathway] = useState("unengaged");
  const [donorMode, setDonorMode] = useState("create_and_update");

  // Step 5 state
  const [results, setResults] = useState(null);

  // ── Auto-analyze if preloaded content is provided ───────────
  const autoAnalyzed = React.useRef(false);
  useEffect(() => {
    if (preloadedContent && !autoAnalyzed.current) {
      autoAnalyzed.current = true;
      handleAnalyze();
    }
  }, [preloadedContent]);

  // ── Step 1: File Upload ─────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => setFileContent(evt.target.result);
    reader.readAsText(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => setFileContent(evt.target.result);
    reader.readAsText(file);
  }, []);

  // ── Step 2: Analyze ─────────────────────────────────────────
  const handleAnalyze = async () => {
    if (!fileContent) return;
    setAnalyzing(true);
    try {
      const { headers, rows } = parseCSV(fileContent);
      const mapping = autoMapColumns(headers);
      const diag = diagnoseImport(rows, mapping);
      setDiagnostic(diag);
      const result = await analyzeImport(rows, mapping);
      result.mapping = mapping;
      result.rows = rows;
      setFullAnalysis(result);
      setAnalysis(result);
      setStep(2);
    } catch (err) {
      showMessage("Failed to analyze CSV: " + err.message, "error");
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Step 3: Scan for duplicates ─────────────────────────────
  const handleScanDuplicates = async () => {
    if (!analysis) return;
    setScanningDuplicates(true);
    try {
      const dupes = await findDuplicateGifts(analysis);
      setDuplicates(dupes);
      // Default: skip all duplicates (unchecked = skip)
      setSkipSet(new Set(dupes.map((d) => d.rowIndex)));
      setStep(3);
    } catch (err) {
      showMessage("Failed to scan for duplicates: " + err.message, "error");
    } finally {
      setScanningDuplicates(false);
    }
  };

  // ── Apply date range filter ────────────────────────────────
  const applyDateFilter = (from, to) => {
    setFilterFrom(from);
    setFilterTo(to);
    if (!fullAnalysis) return;
    if (!from && !to) {
      setAnalysis(fullAnalysis);
    } else {
      setAnalysis(filterAnalysisByDate(fullAnalysis, from || null, to || null));
    }
  };

  // ── Skip duplicate scan (fresh import) ─────────────────────
  const handleSkipScan = () => {
    setDuplicates([]);
    setSkipSet(new Set());
    setStep(3);
  };

  // ── Duplicate review helpers ────────────────────────────────
  const toggleDuplicate = (rowIndex) => {
    setSkipSet((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
  };

  const selectAllDuplicates = () => {
    // "Select All" means import all — clear skip set
    setSkipSet(new Set());
  };

  const deselectAllDuplicates = () => {
    // "Deselect All" means skip all
    setSkipSet(new Set(duplicates.map((d) => d.rowIndex)));
  };

  // ── Step 4: Execute Import ──────────────────────────────────
  const handleImport = async () => {
    if (!analysis) return;
    setImporting(true);
    setStep(4);
    try {
      const result = await executeImport(
        analysis,
        {
          chapterId: CHAPTER_ID,
          userId: currentUser?.id,
          skipIndices: skipSet,
          defaultPathway,
          donorMode,
          batchId: batchId || null,
        },
        (prog) => setProgress(prog)
      );
      setResults(result);
      setStep(5);
      bumpDataVersion();
      if (onComplete) onComplete(result);
    } catch (err) {
      showMessage("Import failed: " + err.message, "error");
      setStep(3);
    } finally {
      setImporting(false);
    }
  };

  // ── Shared: stat badge ──────────────────────────────────────
  const stat = (label, value, color) => (
    <div
      style={{
        textAlign: "center",
        padding: "12px 16px",
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 8,
        flex: "1 1 120px",
      }}
    >
      <div
        style={{
          fontSize: FONT_SIZES.xl,
          fontWeight: 700,
          color: color || palette.text1,
          fontFamily: FONT_FAMILY,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 600,
          textTransform: "uppercase",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );

  // ── Shared: format currency ─────────────────────────────────
  const fmtCurrency = (amount) =>
    "$" + Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Render ──────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: palette.cardBg,
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          width: "100%",
          maxWidth: 750,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "14px 20px",
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: FONT_SIZES.md,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            <Upload size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
            Import Donors & Gifts
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Step indicator */}
            <div
              style={{
                display: "flex",
                gap: 4,
                fontSize: "0.6rem",
                fontWeight: 600,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
              }}
            >
              {STEP_LABELS.map((s, idx) => (
                <span
                  key={s}
                  style={{
                    padding: "2px 8px",
                    borderRadius: 10,
                    background: step === idx + 1 ? palette.accent1 : `${palette.text3}15`,
                    color: step === idx + 1 ? "#fff" : palette.text3,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
            {step !== 4 && (
              <button
                onClick={onClose}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: palette.text3,
                  padding: 4,
                }}
              >
                <X size={ICON_SIZES.md} />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {/* ── STEP 1: Upload ─────────────────────────────── */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text2,
                  fontFamily: FONT_FAMILY,
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Upload a Spektrix CSV export. The file should contain donor
                transaction rows — each row is a gift. Donors will be
                deduplicated automatically by Facilitator ID.
              </p>

              {/* Drop zone */}
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${fileContent ? "#16a34a" : palette.cardBorder}`,
                  borderRadius: 10,
                  padding: "40px 20px",
                  textAlign: "center",
                  background: fileContent ? "#f0fdf4" : `${palette.accent1}04`,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onClick={() => document.getElementById("csv-file-input")?.click()}
              >
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
                {fileContent ? (
                  <>
                    <FileText
                      size={ICON_SIZES.xxl}
                      style={{ color: "#16a34a", marginBottom: 8 }}
                    />
                    <div
                      style={{
                        fontSize: FONT_SIZES.sm,
                        fontWeight: 600,
                        color: "#16a34a",
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      {fileName}
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        fontFamily: FONT_FAMILY,
                        marginTop: 4,
                      }}
                    >
                      Click to choose a different file
                    </div>
                  </>
                ) : (
                  <>
                    <Upload
                      size={ICON_SIZES.xxl}
                      style={{ color: palette.text3, marginBottom: 8 }}
                    />
                    <div
                      style={{
                        fontSize: FONT_SIZES.sm,
                        color: palette.text2,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      Drop CSV file here or click to browse
                    </div>
                  </>
                )}
              </div>

              {/* Next button */}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <ActionButton
                  label={analyzing ? "Analyzing..." : "Analyze File"}
                  icon={analyzing ? Loader : ArrowRight}
                  onClick={handleAnalyze}
                  disabled={!fileContent || analyzing}
                />
              </div>
            </div>
          )}

          {/* ── STEP 2: Preview ────────────────────────────── */}
          {step === 2 && analysis && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Stats */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {stat("Total Rows", analysis.totalRows.toLocaleString())}
                {stat("Unique Donors", analysis.uniqueDonors.toLocaleString(), palette.accent1)}
                {stat("New Donors", analysis.newDonors.toLocaleString(), "#16a34a")}
                {stat("Existing", analysis.existingDonors.toLocaleString(), "#f59e0b")}
                {stat("Gifts", analysis.totalGifts.toLocaleString(), "#3b82f6")}
              </div>

              {/* Date range filter */}
              <div
                style={{
                  padding: "12px 16px",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                  background: `${palette.accent1}04`,
                }}
              >
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    marginBottom: 8,
                  }}
                >
                  Date Range Filter — import a subset of the file
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="date"
                    value={filterFrom}
                    onChange={(e) => applyDateFilter(e.target.value, filterTo)}
                    style={{
                      padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
                      background: palette.inputBg, color: palette.inputColor,
                    }}
                  />
                  <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>to</span>
                  <input
                    type="date"
                    value={filterTo}
                    onChange={(e) => applyDateFilter(filterFrom, e.target.value)}
                    style={{
                      padding: "4px 8px", borderRadius: 6, fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY, border: `1px solid ${palette.inputBorder}`,
                      background: palette.inputBg, color: palette.inputColor,
                    }}
                  />
                  {(filterFrom || filterTo) && (
                    <button
                      onClick={() => applyDateFilter("", "")}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: palette.accent1, fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY, fontWeight: 600, padding: 0,
                      }}
                    >
                      Clear Filter
                    </button>
                  )}
                  {fullAnalysis && analysis.totalGifts !== fullAnalysis.totalGifts && (
                    <span style={{
                      fontSize: FONT_SIZES.xs, color: palette.accent1,
                      fontWeight: 600, fontFamily: FONT_FAMILY,
                    }}>
                      Showing {analysis.totalGifts.toLocaleString()} of {fullAnalysis.totalGifts.toLocaleString()} gifts
                    </span>
                  )}
                </div>
                {analysis.dateRange && (
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                      fontFamily: FONT_FAMILY,
                      marginTop: 8,
                    }}
                  >
                    Data range: <strong>{analysis.dateRange.earliest}</strong> to{" "}
                    <strong>{analysis.dateRange.latest}</strong>
                    &nbsp;&nbsp;·&nbsp;&nbsp;
                    {analysis.uniqueFunds.length} unique fund{analysis.uniqueFunds.length !== 1 ? "s" : ""}
                    {analysis.skippedNoId > 0 && (
                      <span style={{ color: "#f59e0b" }}>
                        &nbsp;&nbsp;·&nbsp;&nbsp;{analysis.skippedNoId} rows skipped (no ID)
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Funds list */}
              {analysis.uniqueFunds.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: palette.text3,
                      fontFamily: FONT_FAMILY,
                      marginBottom: 4,
                    }}
                  >
                    Funds (will auto-create if new)
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    {analysis.uniqueFunds.map((f) => (
                      <span
                        key={f}
                        style={{
                          padding: "2px 8px",
                          borderRadius: 8,
                          background: `${palette.accent1}10`,
                          color: palette.accent1,
                          fontWeight: 600,
                        }}
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Diagnostic: rows that will be skipped */}
              {diagnostic && (diagnostic.noSpektrixId.length > 0 || diagnostic.noAmount.length > 0 || diagnostic.zeroAmount.length > 0 || diagnostic.noDate.length > 0 || diagnostic.badDate.length > 0) && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: FONT_SIZES.sm,
                      fontWeight: 600,
                      color: "#92400e",
                      fontFamily: FONT_FAMILY,
                      marginBottom: 6,
                    }}
                  >
                    <AlertCircle size={ICON_SIZES.sm} />
                    {diagnostic.noSpektrixId.length + diagnostic.noAmount.length + diagnostic.zeroAmount.length + diagnostic.noDate.length + diagnostic.badDate.length} rows
                    will be skipped:
                  </div>
                  <div style={{ fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY, color: "#78350f", lineHeight: 1.6 }}>
                    {diagnostic.noSpektrixId.length > 0 && (
                      <div>No Facilitator ID: <strong>{diagnostic.noSpektrixId.length}</strong> rows</div>
                    )}
                    {diagnostic.noAmount.length > 0 && (
                      <div>Blank amount: <strong>{diagnostic.noAmount.length}</strong> rows</div>
                    )}
                    {diagnostic.zeroAmount.length > 0 && (
                      <div>Zero/negative amount: <strong>{diagnostic.zeroAmount.length}</strong> rows</div>
                    )}
                    {diagnostic.noDate.length > 0 && (
                      <div>Blank date: <strong>{diagnostic.noDate.length}</strong> rows</div>
                    )}
                    {diagnostic.badDate.length > 0 && (
                      <div>
                        Unparseable date: <strong>{diagnostic.badDate.length}</strong> rows
                        {diagnostic.badDate.length <= 5 && (
                          <span style={{ color: "#b45309" }}>
                            {" "}— samples: {diagnostic.badDate.map((d) => `"${d.raw}"`).join(", ")}
                          </span>
                        )}
                        {diagnostic.badDate.length > 5 && (
                          <span style={{ color: "#b45309" }}>
                            {" "}— samples: {diagnostic.badDate.slice(0, 5).map((d) => `"${d.raw}"`).join(", ")}...
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ marginTop: 4, color: "#92400e" }}>
                      <strong>{diagnostic.valid}</strong> rows will be imported.
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <ActionButton
                  label="Back"
                  icon={ArrowLeft}
                  variant="secondary"
                  onClick={() => setStep(1)}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <ActionButton
                    label="Skip Scan"
                    variant="secondary"
                    onClick={handleSkipScan}
                    disabled={scanningDuplicates}
                  />
                  <ActionButton
                    label={scanningDuplicates ? "Scanning..." : "Check for Duplicates"}
                    icon={scanningDuplicates ? Loader : ArrowRight}
                    onClick={handleScanDuplicates}
                    disabled={scanningDuplicates}
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Duplicate Review ───────────────────── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {duplicates.length === 0 ? (
                /* No duplicates found */
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <CheckCircle
                    size={ICON_SIZES.xxl}
                    style={{ color: "#16a34a", marginBottom: 8 }}
                  />
                  <div
                    style={{
                      fontSize: FONT_SIZES.md,
                      fontWeight: 700,
                      color: palette.text1,
                      fontFamily: FONT_FAMILY,
                      marginBottom: 4,
                    }}
                  >
                    No duplicates detected
                  </div>
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    All {analysis?.totalGifts.toLocaleString()} gift transactions
                    are new and ready to import.
                  </div>
                </div>
              ) : (
                /* Duplicates found */
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      borderRadius: 8,
                    }}
                  >
                    <AlertTriangle size={ICON_SIZES.sm} style={{ color: "#d97706", flexShrink: 0 }} />
                    <div
                      style={{
                        fontSize: FONT_SIZES.sm,
                        color: "#92400e",
                        fontFamily: FONT_FAMILY,
                        lineHeight: 1.4,
                      }}
                    >
                      <strong>{duplicates.length} potential duplicate{duplicates.length !== 1 ? "s" : ""}</strong>{" "}
                      found out of {analysis?.totalGifts.toLocaleString()} total gifts.
                      These match an existing gift with the same donor, date, and amount.
                      Check the box to import anyway, or leave unchecked to skip.
                    </div>
                  </div>

                  {/* Select All / Deselect All */}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      fontSize: FONT_SIZES.xs,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    <button
                      onClick={selectAllDuplicates}
                      style={{
                        background: "none",
                        border: `1px solid ${palette.inputBorder}`,
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        color: palette.text2,
                        cursor: "pointer",
                      }}
                    >
                      Import All
                    </button>
                    <button
                      onClick={deselectAllDuplicates}
                      style={{
                        background: "none",
                        border: `1px solid ${palette.inputBorder}`,
                        borderRadius: 6,
                        padding: "4px 10px",
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        color: palette.text2,
                        cursor: "pointer",
                      }}
                    >
                      Skip All
                    </button>
                    <span
                      style={{
                        color: palette.text3,
                        padding: "4px 0",
                        marginLeft: 4,
                      }}
                    >
                      {skipSet.size} will be skipped · {duplicates.length - skipSet.size} will be imported
                    </span>
                  </div>

                  {/* Duplicate table */}
                  <div
                    style={{
                      maxHeight: 320,
                      overflowY: "auto",
                      border: `1px solid ${palette.cardBorder}`,
                      borderRadius: 8,
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            position: "sticky",
                            top: 0,
                            background: palette.cardBg,
                            borderBottom: `2px solid ${palette.cardBorder}`,
                          }}
                        >
                          <th style={{ padding: "8px 10px", textAlign: "center", width: 40, color: palette.text3, fontWeight: 600 }}>
                            Import?
                          </th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: palette.text3, fontWeight: 600 }}>
                            Donor
                          </th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: palette.text3, fontWeight: 600 }}>
                            Date
                          </th>
                          <th style={{ padding: "8px 10px", textAlign: "right", color: palette.text3, fontWeight: 600 }}>
                            Amount
                          </th>
                          <th style={{ padding: "8px 10px", textAlign: "left", color: palette.text3, fontWeight: 600 }}>
                            Fund
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {duplicates.map((dup) => {
                          const willSkip = skipSet.has(dup.rowIndex);
                          return (
                            <tr
                              key={dup.rowIndex}
                              style={{
                                borderBottom: `1px solid ${palette.cardBorder}`,
                                background: willSkip ? `${palette.text3}06` : "#fefce8",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleDuplicate(dup.rowIndex)}
                            >
                              <td style={{ padding: "6px 10px", textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={!willSkip}
                                  onChange={() => toggleDuplicate(dup.rowIndex)}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ cursor: "pointer" }}
                                />
                              </td>
                              <td style={{ padding: "6px 10px", color: palette.text1 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <AlertTriangle size={ICON_SIZES.xs} style={{ color: "#d97706", flexShrink: 0 }} />
                                  {dup.donorName}
                                </div>
                              </td>
                              <td style={{ padding: "6px 10px", color: palette.text2 }}>
                                {dup.giftDate}
                              </td>
                              <td style={{ padding: "6px 10px", textAlign: "right", color: palette.text1, fontWeight: 600 }}>
                                {fmtCurrency(dup.amount)}
                              </td>
                              <td style={{ padding: "6px 10px", color: palette.text2 }}>
                                {dup.fundName || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Import Options */}
              <div
                style={{
                  padding: "12px 16px",
                  border: `1px solid ${palette.cardBorder}`,
                  borderRadius: 8,
                }}
              >
                <div
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: palette.text3,
                    fontFamily: FONT_FAMILY,
                    marginBottom: 8,
                  }}
                >
                  Import Options
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    fontSize: FONT_SIZES.xs,
                    fontFamily: FONT_FAMILY,
                    color: palette.text1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>Donor handling:</span>
                    <select
                      value={donorMode}
                      onChange={(e) => setDonorMode(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        border: `1px solid ${palette.inputBorder}`,
                        background: palette.inputBg,
                        color: palette.inputColor,
                        cursor: "pointer",
                      }}
                    >
                      <option value="create_and_update">Create new + update existing</option>
                      <option value="skip_existing">Create new only (skip existing)</option>
                      <option value="update_only">Update existing only (skip new)</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>Default pathway for new donors:</span>
                    <select
                      value={defaultPathway}
                      onChange={(e) => setDefaultPathway(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        fontSize: FONT_SIZES.xs,
                        fontFamily: FONT_FAMILY,
                        border: `1px solid ${palette.inputBorder}`,
                        background: palette.inputBg,
                        color: palette.inputColor,
                        cursor: "pointer",
                      }}
                    >
                      {pathways.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <ActionButton
                  label="Back"
                  icon={ArrowLeft}
                  variant="secondary"
                  onClick={() => setStep(2)}
                />
                <ActionButton
                  label="Start Import"
                  icon={ArrowRight}
                  onClick={handleImport}
                />
              </div>
            </div>
          )}

          {/* ── STEP 4: Importing ──────────────────────────── */}
          {step === 4 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
              }}
            >
              <Loader
                size={ICON_SIZES.xxl}
                style={{
                  color: palette.accent1,
                  marginBottom: 16,
                  animation: "spin 1s linear infinite",
                }}
              />
              <div
                style={{
                  fontSize: FONT_SIZES.md,
                  fontWeight: 700,
                  color: palette.text1,
                  fontFamily: FONT_FAMILY,
                  marginBottom: 8,
                }}
              >
                Importing...
              </div>
              {progress && (
                <>
                  <div
                    style={{
                      width: "100%",
                      height: 8,
                      background: `${palette.text3}20`,
                      borderRadius: 4,
                      overflow: "hidden",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round((progress.current / progress.total) * 100)}%`,
                        height: "100%",
                        background: palette.accent1,
                        borderRadius: 4,
                        transition: "width 0.3s",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                      fontFamily: FONT_FAMILY,
                    }}
                  >
                    Donor {progress.current} of {progress.total}
                    &nbsp;&nbsp;·&nbsp;&nbsp;
                    {progress.donorsCreated} created
                    &nbsp;&nbsp;·&nbsp;&nbsp;
                    {progress.giftsCreated} gifts
                  </div>
                </>
              )}
              <p
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  marginTop: 16,
                }}
              >
                Do not close this window. Large imports may take several minutes.
              </p>
            </div>
          )}

          {/* ── STEP 5: Results ────────────────────────────── */}
          {step === 5 && results && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ textAlign: "center", marginBottom: 8 }}>
                <CheckCircle
                  size={ICON_SIZES.xxl}
                  style={{ color: "#16a34a", marginBottom: 8 }}
                />
                <div
                  style={{
                    fontSize: FONT_SIZES.lg,
                    fontWeight: 700,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                  }}
                >
                  Import Complete
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {stat("Donors Created", results.donorsCreated, "#16a34a")}
                {stat("Donors Updated", results.donorsUpdated, "#3b82f6")}
                {stat("Donors Skipped", results.donorsSkipped, "#6b7280")}
                {results.donorsFailedLookup > 0 && stat("Donor Lookup Failed", results.donorsFailedLookup, "#dc2626")}
                {stat("Gifts Created", results.giftsCreated, palette.accent1)}
                {stat("Gifts Skipped", results.giftsSkipped, "#f59e0b")}
              </div>

              {results.errors.length > 0 && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: FONT_SIZES.sm,
                      fontWeight: 600,
                      color: "#dc2626",
                      fontFamily: FONT_FAMILY,
                      marginBottom: 8,
                    }}
                  >
                    <AlertCircle size={ICON_SIZES.sm} />
                    {results.errors.length} error{results.errors.length !== 1 ? "s" : ""}
                  </div>
                  <div style={{ maxHeight: 150, overflowY: "auto" }}>
                    {results.errors.slice(0, 20).map((err, idx) => (
                      <div
                        key={idx}
                        style={{
                          fontSize: FONT_SIZES.xs,
                          color: "#991b1b",
                          fontFamily: FONT_FAMILY,
                          padding: "2px 0",
                          borderBottom: "1px solid #fecaca",
                        }}
                      >
                        ID {err.spektrixId}: {err.error}
                      </div>
                    ))}
                    {results.errors.length > 20 && (
                      <div
                        style={{
                          fontSize: FONT_SIZES.xs,
                          color: "#991b1b",
                          fontStyle: "italic",
                          marginTop: 4,
                        }}
                      >
                        ...and {results.errors.length - 20} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <ActionButton label="Done" onClick={onClose} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
